/**
 * Replaces placeholders in a template string with actual data values
 * Uses the same logic as the backend placeholder replacement system
 * @param template - The template string containing {{placeholder}} patterns
 * @param data - The data object containing the values to replace placeholders with
 * @returns The template string with placeholders replaced by actual values
 */
export function replacePlaceholders(
	template: string,
	data: Record<string, unknown>,
): string {
	return template.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (match, placeholder) => {
		const keys = placeholder.trim().split(".");
		let value: unknown = data;

		for (const key of keys) {
			if (value && typeof value === "object" && key in value) {
				value = (value as Record<string, unknown>)[key];
			} else {
				return match;
			}
		}

		return String(value || "");
	});
}

/**
 * Adds static data that should be available in all templates
 * @param data - The existing data object
 * @returns The data object with additional static data
 */
export function addStaticData(
	data: Record<string, unknown>,
): Record<string, unknown> {
	return {
		...data,
		DATE_DU_JOUR: new Date().toLocaleDateString("fr-FR"),
		HEURE_ACTUELLE: new Date().toLocaleTimeString("fr-FR", {
			hour: "2-digit",
			minute: "2-digit",
		}),
	};
}

/**
 * Extracts all placeholders from a template string
 * @param template - The template string to analyze
 * @returns Array of unique placeholder names found in the template
 */
export function extractPlaceholders(template: string): string[] {
	const placeholders = new Set<string>();
	const regex = /\{\{\s*([^}]+?)\s*\}\}/g;
	let match: RegExpExecArray | null = regex.exec(template);

	while (match !== null) {
		const placeholder = match[1].trim();
		if (!placeholder.startsWith("/") && !placeholder.startsWith("#")) {
			placeholders.add(placeholder);
		}
		match = regex.exec(template);
	}

	return Array.from(placeholders);
}

/**
 * Validates if all placeholders in a template can be resolved with the provided data
 * @param template - The template string to validate
 * @param data - The data object to check against
 * @returns Object containing validation results
 */
export function validateTemplatePlaceholders(
	template: string,
	data: Record<string, unknown>,
): {
	isValid: boolean;
	missingPlaceholders: string[];
	totalPlaceholders: number;
} {
	const placeholders = extractPlaceholders(template);
	const missingPlaceholders: string[] = [];

	for (const placeholder of placeholders) {
		const keys = placeholder.split(".");
		let value: unknown = data;
		let found = true;

		for (const key of keys) {
			if (value && typeof value === "object" && key in value) {
				value = (value as Record<string, unknown>)[key];
			} else {
				found = false;
				break;
			}
		}

		if (!found) {
			missingPlaceholders.push(placeholder);
		}
	}

	return {
		isValid: missingPlaceholders.length === 0,
		missingPlaceholders,
		totalPlaceholders: placeholders.length,
	};
}
